import React, {useEffect, useRef, useState} from 'react';
import { X, Download, Copy } from 'lucide-react';
import { TableData } from '../utils/types';

interface TableModalProps {
    data: TableData;
    isOpen: boolean;
    onClose: () => void;
}

const TableModal: React.FC<TableModalProps> = ({ data, isOpen, onClose }) => {
    const [copySuccess, setCopySuccess] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);

    const parseCSVData = (csvData: string) => {
        const rows = csvData.trim().split('\n');
        const headers = rows[0].split(',');

        const parsedData = rows.slice(1).map(row => {
            const values = row.split(',');
            const rowData: Record<string, string> = {};

            headers.forEach((header, index) => {
                rowData[header] = values[index] || '';
            });

            return rowData;
        });

        return { headers, rows: parsedData };
    };

    const parseData = () => {
        if (data.type === 'csv') {
            return parseCSVData(data.data);
        } else if (data.type === 'json') {
            try {
                // Ensure we have a JS object to work with
                const jsonData = typeof data.data === 'string'
                    ? JSON.parse(data.data)
                    : data.data;

                console.log("TableModal processing JSON data:", jsonData);

                if (Array.isArray(jsonData)) {
                    if (jsonData.length === 0) {
                        return { headers: [], rows: [] };
                    }

                    // Get all possible headers from all objects
                    const headers = Array.from(
                        new Set(
                            jsonData.flatMap(obj => Object.keys(obj))
                        )
                    );

                    // Create rows with all headers
                    const rows = jsonData.map(obj => {
                        const row: Record<string, string> = {};
                        headers.forEach(header => {
                            const value = obj[header];
                            row[header] = value !== undefined && value !== null
                                ? (typeof value === 'object' ? JSON.stringify(value) : String(value))
                                : '';
                        });
                        return row;
                    });

                    return { headers, rows };
                }

                if (typeof jsonData === 'object' && jsonData !== null) {
                    const headers = Object.keys(jsonData);
                    const rows = [
                        headers.reduce((row, header) => {
                            const value = jsonData[header];
                            row[header] = value !== undefined && value !== null
                                ? (typeof value === 'object' ? JSON.stringify(value) : String(value))
                                : '';
                            return row;
                        }, {} as Record<string, string>)
                    ];
                    return { headers, rows };
                }
            } catch (error) {
                console.error('Error parsing JSON data in modal:', error);
                // If there's an error, try to display the raw data
                return {
                    headers: ['Raw Data'],
                    rows: [{ 'Raw Data': typeof data.data === 'string' ? data.data : JSON.stringify(data.data) }]
                };
            }
        }

        return { headers: [], rows: [] };
    };

    const { headers, rows } = parseData();

    const copyToClipboard = () => {
        let textToCopy;
        
        if (data.type === 'json') {
            if (typeof data.data === 'string') {
                try {
                    // Prettify JSON if it's a string
                    textToCopy = JSON.stringify(JSON.parse(data.data), null, 2);
                } catch (e) {
                    console.error('Error formatting JSON for clipboard:', e);
                    textToCopy = data.data;
                }
            } else {
                // It's already a JavaScript object, stringify it
                textToCopy = JSON.stringify(data.data, null, 2);
            }
        } else {
            // CSV or other formats
            textToCopy = data.data;
        }

        navigator.clipboard.writeText(textToCopy)
            .then(() => {
                console.log('Successfully copied to clipboard');
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            })
            .catch(err => {
                console.error('Failed to copy text: ', err);
                alert('Failed to copy to clipboard');
            });
    };

    const downloadData = () => {
        let content = data.data;
        let mimeType = 'text/csv';
        let fileExtension = 'csv';

        if (data.type === 'json') {
            if (typeof data.data === 'string') {
                try {
                    // Pretty print JSON if it's a string
                    content = JSON.stringify(JSON.parse(data.data), null, 2);
                } catch (e) {
                    console.error('Error formatting JSON for download:', e);
                }
            } else {
                content = JSON.stringify(data.data, null, 2);
            }
            mimeType = 'application/json';
            fileExtension = 'json';
        }

        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${data.title || 'data'}.${fileExtension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
        }

        return () => {
            document.removeEventListener('keydown', handleEsc);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div
                ref={modalRef}
                className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col"
            >
                <div className="flex items-center justify-between bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-lg">
                    <h3 className="text-xl font-semibold text-gray-800">
                        {data.title || 'Table Data'}
                    </h3>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={copyToClipboard}
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="Copy to clipboard"
                        >
                            {copySuccess ? (
                                <span className="text-sm text-green-600 flex items-center">
                                    <span className="ml-1">Copied!</span>
                                </span>
                            ) : (
                                <Copy size={20} />
                            )}
                        </button>
                        <button
                            onClick={downloadData}
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title={`Download ${data.type.toUpperCase()}`}
                        >
                            <Download size={20} />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Close"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-auto">
                    <div className="p-6">
                        <div className="overflow-x-auto">
                            {headers.length > 0 ? (
                                <table className="w-full text-left table-auto border-collapse">
                                    <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                        {headers.map((header, index) => (
                                            <th
                                                key={index}
                                                className="text-xs p-3 font-b text-white border border-gray-200 bg-blue-500"
                                            >
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {rows.map((row, rowIndex) => (
                                        <tr
                                            key={rowIndex}
                                            className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-blue-50'}
                                        >
                                            {headers.map((header, colIndex) => (
                                                <td
                                                    key={colIndex}
                                                    className="px-4 py-3 border border-gray-200 text-xs"
                                                >
                                                    {row[header]}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="p-8 text-center text-gray-500">
                                    <p>No data available to display</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 text-right rounded-b-lg">
                    <span className="text-sm text-gray-500">
                        Showing {rows.length} rows and {headers.length} columns
                    </span>
                </div>
            </div>
        </div>
    );
};

export default TableModal;